import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { format, formatDistanceToNow } from 'date-fns';

// Schedule presets
const schedulePresets = [
  { label: 'Daily at 2:00 AM', value: '02:00' },
  { label: 'Daily at 3:00 AM', value: '03:00' },
  { label: 'Daily at 4:00 AM', value: '04:00' },
  { label: 'Daily at 5:00 AM', value: '05:00' },
  { label: 'Daily at 6:00 AM', value: '06:00' },
  { label: 'Daily at 12:00 PM', value: '12:00' },
  { label: 'Daily at 6:00 PM', value: '18:00' },
  { label: 'Every 6 hours', value: 'every6h' },
  { label: 'Every 12 hours', value: 'every12h' },
  { label: 'Weekly (Sunday at 1:00 AM)', value: 'weekly_sun' },
  { label: 'Weekly (Monday at 2:00 AM)', value: 'weekly_mon' },
  { label: 'Monthly (1st at 12:00 AM)', value: 'monthly' },
  { label: 'Custom Time', value: 'custom' }
];

const timezones = [
  'Asia/Kolkata',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney',
  'Asia/Dubai',
  'Asia/Singapore'
];

const POLLING_INTERVAL = 30000;
const RESTORE_POLLING_INTERVAL = 3000; // 3 seconds
const MAX_RESTORE_ATTEMPTS = 120; // 120 * 3s = 6 minutes

const BackupManagement = () => {
  // ============ State ============
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('backups');
  const [isLoading, setIsLoading] = useState(false);
  const [, setDebugInfo] = useState(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Restore state
  const [restoringBackupId, setRestoringBackupId] = useState(null);
  const [restoreStatus, setRestoreStatus] = useState(null);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreMessage, setRestoreMessage] = useState('');

  const pollingIntervalRef = useRef(null);
  const restorePollingRef = useRef(null);
  const isPollingRef = useRef(false);

  const [settings, setSettings] = useState({
    scheduleTime: '02:00',
    isEnabled: false,
    timezone: 'Asia/Kolkata',
    retentionDays: 7,
    scheduleFrequency: 'daily'
  });

  const [customHour, setCustomHour] = useState(2);
  const [customMinute, setCustomMinute] = useState(0);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: '',
    backupId: '',
    message: ''
  });

  const [toast, setToast] = useState({
    open: false,
    message: '',
    type: 'success'
  });

  const [logs, setLogs] = useState('');
  const [logsDate, setLogsDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [logsLoading, setLogsLoading] = useState(false);

  // ============ Helper Functions ============
  const formatTimeDisplay = (time) => {
    if (!time) return '2:00 AM';
    if (['every6h', 'every12h', 'weekly_sun', 'weekly_mon', 'monthly'].includes(time)) {
      return schedulePresets.find(p => p.value === time)?.label || time;
    }
    const parts = time.split(':');
    const hour = parseInt(parts[0]);
    const minute = parseInt(parts[1]) || 0;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const showToast = (message, type = 'success') => {
    setToast({ open: true, message, type });
    setTimeout(() => setToast({ open: false, message: '', type: 'success' }), 5000);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'pending': return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'in-progress': return 'bg-sky-50 text-sky-700 border border-sky-200';
      case 'failed': return 'bg-rose-50 text-rose-700 border border-rose-200';
      default: return 'bg-slate-50 text-slate-700 border border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'in-progress': 'In Progress',
      'completed': 'Completed',
      'failed': 'Failed'
    };
    return labels[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'MMM dd, yyyy HH:mm:ss');
  };

  const getTimeAgo = (date) => {
    if (!date) return 'N/A';
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  // ============ API Calls ============
  const loadDashboardData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const [statsRes, backupsRes, schedulerRes] = await Promise.all([
        api.get('/api/super-admin/backups/stats'),
        api.get('/api/super-admin/backups', { params: { page: 1, limit: 50 } }),
        api.get('/api/super-admin/scheduler/status')
      ]);

      setDebugInfo({
        stats: statsRes.data,
        backups: backupsRes.data,
        scheduler: schedulerRes.data,
        timestamp: new Date().toISOString()
      });

      setStats(statsRes.data.data);
      
      let backupList = [];
      if (backupsRes.data.data?.backups) {
        backupList = backupsRes.data.data.backups;
      } else if (backupsRes.data.backups) {
        backupList = backupsRes.data.backups;
      } else if (Array.isArray(backupsRes.data.data)) {
        backupList = backupsRes.data.data;
      }
      setBackups(backupList);

      const schedulerData = schedulerRes.data.data || schedulerRes.data;
      setScheduler(schedulerData);
      
      if (schedulerData) {
        const settingsData = schedulerData.settings || schedulerData;
        
        let scheduleTime = settingsData.scheduleTime || '02:00';
        if (!scheduleTime && settingsData.cronExpression) {
          const parts = settingsData.cronExpression.split(' ');
          const minute = parts[0] || '0';
          const hour = parts[1] || '2';
          scheduleTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        }
        
        setSettings({
          scheduleTime: scheduleTime,
          isEnabled: settingsData.isEnabled || false,
          timezone: settingsData.timezone || 'Asia/Kolkata',
          retentionDays: settingsData.retentionDays || 7,
          scheduleFrequency: settingsData.scheduleFrequency || 'daily'
        });

        const standardValues = ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00', 'every6h', 'every12h', 'weekly_sun', 'weekly_mon', 'monthly'];
        setIsCustomMode(!standardValues.includes(scheduleTime));
        
        if (!standardValues.includes(scheduleTime)) {
          const timeParts = scheduleTime.split(':');
          setCustomHour(parseInt(timeParts[0]) || 2);
          setCustomMinute(parseInt(timeParts[1]) || 0);
        }
      }
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load backup data');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // ============ Restore Status Polling ============
  const pollRestoreStatus = useCallback(async (backupId) => {
    if (!backupId) return;

    try {
      const response = await api.get(`/api/super-admin/backups/${backupId}/restore-status`);
      const data = response.data.data;

      console.log('Restore status response:', data);

      setRestoreStatus(data.status);
      setRestoreProgress(data.progress || 0);
      setRestoreMessage(data.message || '');

      // ✅ Check for completion
      if (data.status === 'completed') {
        setRestoringBackupId(null);
        showToast(`Backup ${backupId} restored successfully!`, 'success');
        await loadDashboardData(false);
        
        if (restorePollingRef.current) {
          clearInterval(restorePollingRef.current);
          restorePollingRef.current = null;
        }
        
        setTimeout(() => {
          setRestoreStatus(null);
          setRestoreProgress(0);
          setRestoreMessage('');
        }, 5000);
        
        await loadDashboardData(true);
        return;
      } 
      
      if (data.status === 'failed') {
        setRestoringBackupId(null);
        showToast(`Restore failed: ${data.message || 'Unknown error'}`, 'error');
        if (restorePollingRef.current) {
          clearInterval(restorePollingRef.current);
          restorePollingRef.current = null;
        }
        setTimeout(() => {
          setRestoreStatus(null);
          setRestoreProgress(0);
          setRestoreMessage('');
        }, 5000);
        return;
      } 
      
      if (data.status === 'idle' && data.restoreCount > 0) {
        setRestoringBackupId(null);
        setRestoreStatus('completed');
        setRestoreProgress(100);
        setRestoreMessage('Restore completed successfully');
        
        if (restorePollingRef.current) {
          clearInterval(restorePollingRef.current);
          restorePollingRef.current = null;
        }
        
        setTimeout(() => {
          setRestoreStatus(null);
          setRestoreProgress(0);
          setRestoreMessage('');
        }, 5000);
        
        await loadDashboardData(true);
        return;
      }

      if (data.status === 'processing') {
        console.log(`Restore in progress: ${data.progress}%`);
      }

    } catch (error) {
      console.error('Error polling restore status:', error);
    }
  }, [loadDashboardData]);

  const startRestorePolling = useCallback((backupId) => {
    if (restorePollingRef.current) {
      clearInterval(restorePollingRef.current);
      restorePollingRef.current = null;
    }

    setRestoringBackupId(backupId);
    setRestoreStatus('processing');
    setRestoreProgress(10);
    setRestoreMessage('Restore started...');

    let attempts = 0;

    pollRestoreStatus(backupId);

    restorePollingRef.current = setInterval(() => {
      attempts++;
      
      if (attempts > MAX_RESTORE_ATTEMPTS) {
        console.log('Restore polling timeout - stopping');
        if (restorePollingRef.current) {
          clearInterval(restorePollingRef.current);
          restorePollingRef.current = null;
        }
        setRestoringBackupId(null);
        setRestoreStatus('timeout');
        setRestoreMessage('Restore is taking longer than expected. Please check the backup list.');
        showToast('Restore is taking longer than expected', 'warning');
        loadDashboardData(true);
        return;
      }
      
      pollRestoreStatus(backupId);
    }, RESTORE_POLLING_INTERVAL);

    return () => {
      if (restorePollingRef.current) {
        clearInterval(restorePollingRef.current);
        restorePollingRef.current = null;
      }
    };
  }, [pollRestoreStatus, loadDashboardData]);

  // ============ Polling Setup ============
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    const shouldPoll = activeTab === 'backups' || 
      backups.some(b => b.status === 'pending' || b.status === 'in-progress');

    if (!shouldPoll) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isPollingRef.current) {
        isPollingRef.current = true;
        loadDashboardData(false).finally(() => {
          isPollingRef.current = false;
        });
      }
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [activeTab, backups, loadDashboardData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const cleanup = startPolling();
    return cleanup;
  }, [startPolling]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (restorePollingRef.current) {
        clearInterval(restorePollingRef.current);
        restorePollingRef.current = null;
      }
    };
  }, []);

  const fetchLogs = async (date) => {
    setLogsLoading(true);
    try {
      const response = await api.get('/api/super-admin/logs', { params: { date } });
      setLogs(response.data.data?.logs || 'No logs found for this date');
    } catch (err) {
      setLogs('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs(logsDate);
    }
  }, [activeTab, logsDate]);

  // ============ Backup Actions ============
  const handleCreateBackup = async () => {
    setIsLoading(true);
    try {
      await api.post('/api/super-admin/backups/create');
      showToast('Backup started successfully!', 'success');
      await loadDashboardData(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create backup', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunBackupNow = async () => {
    setIsLoading(true);
    try {
      await api.post('/api/super-admin/backups/run-now');
      showToast('Backup triggered successfully!', 'success');
      await loadDashboardData(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to trigger backup', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBackup = async () => {
    setIsLoading(true);
    try {
      await api.delete(`/api/super-admin/backups/${confirmDialog.backupId}`);
      showToast('Backup deleted successfully!', 'success');
      setConfirmDialog({ open: false, action: '', backupId: '', message: '' });
      await loadDashboardData(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete backup', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    const backupId = confirmDialog.backupId;
    setIsLoading(true);
    
    try {
      const response = await api.post(`/api/super-admin/backups/${backupId}/restore`, { 
        dropExisting: false 
      });

      if (response.data.success) {
        showToast('Restore started successfully!', 'success');
        setConfirmDialog({ open: false, action: '', backupId: '', message: '' });
        
        startRestorePolling(backupId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to restore backup', 'error');
      setConfirmDialog({ open: false, action: '', backupId: '', message: '' });
    } finally {
      setIsLoading(false);
    }
  };

  // ============ Schedule Actions ============
  const handleToggleSchedule = async () => {
    const newValue = !settings.isEnabled;
    setSettings(prev => ({ ...prev, isEnabled: newValue }));
    setIsLoading(true);
    try {
      const payload = {
        scheduleTime: settings.scheduleTime,
        isEnabled: newValue,
        timezone: settings.timezone,
        retentionDays: settings.retentionDays,
        scheduleFrequency: settings.scheduleFrequency || 'daily'
      };
      await api.put('/api/super-admin/scheduler/update', payload);
      showToast('Schedule status updated!', 'success');
      await loadDashboardData(false);
    } catch (err) {
      setSettings(prev => ({ ...prev, isEnabled: !newValue }));
      showToast(err.response?.data?.message || 'Failed to update schedule', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    setIsLoading(true);
    try {
      const payload = {
        scheduleTime: settings.scheduleTime,
        isEnabled: settings.isEnabled,
        timezone: settings.timezone,
        retentionDays: settings.retentionDays,
        scheduleFrequency: settings.scheduleFrequency || 'daily'
      };
      await api.put('/api/super-admin/scheduler/update', payload);
      showToast('Backup schedule saved successfully!', 'success');
      await loadDashboardData(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update schedule', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadDashboardData(true);
    showToast('Data refreshed!', 'success');
  };

  // ============ Handlers ============
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSettingsChange = (field) => (e) => {
    const value = e.target.value;
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsCustomMode(true);
      const currentCustomTime = `${customHour.toString().padStart(2, '0')}:${customMinute.toString().padStart(2, '0')}`;
      setSettings(prev => ({ ...prev, scheduleTime: currentCustomTime, scheduleFrequency: 'daily' }));
      return;
    }
    setIsCustomMode(false);
    if (['every6h', 'every12h', 'weekly_sun', 'weekly_mon', 'monthly'].includes(value)) {
      setSettings(prev => ({ 
        ...prev, 
        scheduleTime: value,
        scheduleFrequency: value.startsWith('weekly') ? 'weekly' : value === 'monthly' ? 'monthly' : 'daily'
      }));
      return;
    }
    setSettings(prev => ({ ...prev, scheduleTime: value, scheduleFrequency: 'daily' }));
  };

  const handleCustomTimeChange = (type) => (e) => {
    const value = parseInt(e.target.value);
    let nextHour = customHour;
    let nextMinute = customMinute;
    if (type === 'hour') {
      setCustomHour(value);
      nextHour = value;
    } else {
      setCustomMinute(value);
      nextMinute = value;
    }
    const newTime = `${nextHour.toString().padStart(2, '0')}:${nextMinute.toString().padStart(2, '0')}`;
    setSettings(prev => ({ ...prev, scheduleTime: newTime, scheduleFrequency: 'daily' }));
  };

  const openConfirmDialog = (action, backupId, message) => {
    setConfirmDialog({ open: true, action, backupId, message });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: '', backupId: '', message: '' });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'delete') {
      handleDeleteBackup();
    } else if (confirmDialog.action === 'restore') {
      handleRestoreBackup();
    }
  };

  if (loading && !backups.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-100 rounded-full border-t-indigo-600 animate-spin mx-auto"></div>
          <p className="mt-3 text-sm font-medium text-slate-500">Loading backup details...</p>
        </div>
      </div>
    );
  }

  const isSpecialPreset = ['every6h', 'every12h', 'weekly_sun', 'weekly_mon', 'monthly'].includes(settings.scheduleTime);
  const hasActiveBackups = backups.some(b => b.status === 'pending' || b.status === 'in-progress');
  const isRestoring = restoreStatus === 'processing' || restoreStatus === 'pending';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 text-slate-900 antialiased">
      {/* Toast Notification Container */}
      {toast.open && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl transition-all border ${
          toast.type === 'success' ? 'bg-white border-emerald-200 text-emerald-800' : 'bg-white border-rose-200 text-rose-800'
        }`}>
          <div className={`w-2 h-2 rounded-full ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast({ ...toast, open: false })} className="ml-2 text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Restore Status Banner */}
      {isRestoring && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <div>
              <p className="text-sm font-semibold text-indigo-900">
                Restoring backup: {restoringBackupId}
              </p>
              <p className="text-xs text-indigo-700">{restoreMessage || 'Processing...'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-indigo-900">{restoreProgress}%</span>
            <div className="w-32 h-2 bg-indigo-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${restoreProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Header Layout */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-100 pb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950">Backup Management</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Manage database backups, schedule automatic saves, and restore data easily.</span>
            {lastUpdated && <span className="text-slate-300 hidden sm:inline">•</span>}
            {lastUpdated && (
              <span className="text-slate-400 font-medium">Last updated: {format(lastUpdated, 'hh:mm:ss a')}</span>
            )}
          </p>
        </div>
        
        <div className="flex flex-row gap-2 w-full md:w-auto">
          <button
            onClick={handleRefresh}
            disabled={isLoading || isRestoring}
            className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-3.5 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition duration-150 shadow-sm disabled:opacity-50"
          >
            <svg className={`w-4 h-4 text-slate-500 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
          
          <button
            onClick={handleCreateBackup}
            disabled={isLoading || isRestoring}
            className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 active:bg-indigo-800 transition duration-150 shadow-sm disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Backup</span>
          </button>
        </div>
      </div>

      {/* Error Warnings */}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-800 flex items-start justify-between gap-3">
          <div className="flex gap-2.5">
            <svg className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 shrink-0 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Loading Progress Bar */}
      {isLoading && (
        <div className="w-full h-1 bg-indigo-50 rounded-full overflow-hidden relative">
          <div className="h-full bg-indigo-600 rounded-full w-1/3 animate-[infinite_1s_ease-in-out] absolute left-0 top-0" style={{ animationName: 'loading-bar' }}></div>
          <style>{`@keyframes loading-bar { 0% { left: -30%; width: 30%; } 50% { width: 40%; } 100% { left: 100%; width: 20%; } }`}</style>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {[
          { label: 'Total Backups', value: stats?.totalBackups || 0, accent: 'border-slate-200' },
          { label: 'Completed', value: stats?.completedCount || 0, accent: 'border-slate-200 text-emerald-700' },
          { label: 'Failed', value: stats?.failedCount || 0, accent: 'border-slate-200 text-rose-700' },
          { label: 'Restores', value: stats?.totalRestores || 0, accent: 'border-slate-200 text-indigo-700' },
          { label: 'Total Size', value: formatFileSize(stats?.totalSizeBytes || 0), accent: 'border-slate-200 col-span-2 sm:col-span-1' }
        ].map((stat, index) => (
          <div key={index} className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition hover:shadow-md ${stat.accent}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{stat.label}</p>
            <p className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 mt-2">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Main Panel Content */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Navigation Tabs */}
        <div className="border-b border-slate-200 bg-slate-50/70 px-4">
          <div className="flex gap-2 overflow-x-auto no-scrollbar scroll-smooth -mb-px pt-2.5">
            {[
              { id: 'backups', label: 'Backup History' },
              { id: 'schedule', label: 'Schedule Settings' },
              { id: 'logs', label: 'System Logs' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 rounded-t-lg transition relative ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.02)] font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-100/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  {tab.id === 'backups' && hasActiveBackups && (
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ==================== BACKUPS TAB ==================== */}
        {activeTab === 'backups' && (
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex flex-row items-center justify-between pb-2">
              <div>
                <h3 className="text-base font-bold text-slate-900">Saved Checkpoints</h3>
                <p className="text-xs text-slate-500">View logs and details for all saved databases ({backups.length} total)</p>
              </div>
              
              <button
                onClick={handleRunBackupNow}
                disabled={isLoading || isRestoring}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg hover:bg-indigo-100 active:bg-indigo-200 transition disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                </svg>
                <span>Run Backup Now</span>
              </button>
            </div>

            {/* Mobile Cards */}
            <div className="grid grid-cols-1 gap-3 md:hidden">
              {backups.length === 0 ? (
                <div className="py-8 text-center text-xs font-medium text-slate-400 bg-slate-50 rounded-xl border border-dashed">No backups found</div>
              ) : (
                backups
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((backup) => (
                    <div key={backup.backupId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <code className="text-xs font-bold font-mono bg-white border px-2 py-0.5 rounded text-slate-800 tracking-tight block max-w-[180px] truncate">{backup.backupId}</code>
                        <span className={`px-2 py-0.5 text-[11px] font-bold rounded-full ${getStatusColor(backup.status)}`}>
                          {backup.statusLabel || getStatusLabel(backup.status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs text-slate-600">
                        <div><span className="text-slate-400 block text-[10px] uppercase font-semibold">Date Created</span> {formatDate(backup.createdAt)}</div>
                        <div><span className="text-slate-400 block text-[10px] uppercase font-semibold">Age</span> {getTimeAgo(backup.createdAt)}</div>
                        <div><span className="text-slate-400 block text-[10px] uppercase font-semibold">Size</span> {formatFileSize(backup.sizeBytes)}</div>
                        <div><span className="text-slate-400 block text-[10px] uppercase font-semibold">Collections</span> {backup.collections?.length || 0} items</div>
                      </div>

                      {backup.restoreCount > 0 && (
                        <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-2 py-1 rounded-lg">
                          Restored {backup.restoreCount} times
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-2">
                        <button
                          onClick={() => openConfirmDialog('restore', backup.backupId, `Are you sure you want to restore backup ${backup.backupId}? This will overwrite your current staging data.`)}
                          disabled={backup.status !== 'completed' || isLoading || isRestoring}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 border border-slate-200 rounded-lg text-xs font-bold disabled:opacity-40"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => openConfirmDialog('delete', backup.backupId, `Are you sure you want to delete this backup entry: ${backup.backupId}? This cannot be undone.`)}
                          disabled={isLoading || isRestoring}
                          className="px-3 py-1.5 bg-white text-rose-600 border border-slate-200 rounded-lg text-xs font-bold disabled:opacity-40"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 font-semibold bg-slate-50/50">
                    <th className="p-3">Backup ID</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Size</th>
                    <th className="p-3">Collections</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Restores</th>
                    <th className="p-3">Age</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {backups.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-slate-400 font-medium">
                        No history entries found.
                      </td>
                    </tr>
                  ) : (
                    backups
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((backup) => (
                        <tr key={backup.backupId} className="hover:bg-slate-50/70 transition">
                          <td className="p-3 font-mono">
                            <span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-xs font-medium border max-w-[140px] block truncate" title={backup.backupId}>{backup.backupId}</span>
                          </td>
                          <td className="p-3 text-slate-600 font-medium">{formatDate(backup.createdAt)}</td>
                          <td className="p-3 text-slate-900 font-semibold">{formatFileSize(backup.sizeBytes)}</td>
                          <td className="p-3 text-slate-600 font-medium">{backup.collections?.length || 0}</td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full inline-flex items-center ${getStatusColor(backup.status)}`}>
                              {backup.status === 'in-progress' && (
                                <span className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-ping mr-1.5" />
                              )}
                              {backup.statusLabel || getStatusLabel(backup.status)}
                            </span>
                          </td>
                          <td className="p-3 text-slate-600 font-medium">
                            {backup.restoreCount > 0 ? (
                              <span className="text-indigo-600 font-bold">{backup.restoreCount}</span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </td>
                          <td className="p-3 text-slate-500">{getTimeAgo(backup.createdAt)}</td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => openConfirmDialog('restore', backup.backupId, `Are you sure you want to overwrite your active data with backup ${backup.backupId}?`)}
                                disabled={backup.status !== 'completed' || isLoading || isRestoring}
                                className="px-2.5 py-1.5 rounded-lg font-bold text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                              >
                                Restore
                              </button>
                              <button
                                onClick={() => openConfirmDialog('delete', backup.backupId, `Are you sure you want to delete this specific backup: ${backup.backupId}?`)}
                                disabled={isLoading || isRestoring}
                                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {backups.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500">
                  <span>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    className="border border-slate-200 rounded-lg px-2 py-1 bg-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {[5, 10, 25, 50].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handlePageChange(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-2 border rounded-lg text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span className="text-xs sm:text-sm font-medium text-slate-700">
                    Page {page + 1} of {Math.ceil(backups.length / rowsPerPage)}
                  </span>
                  <button
                    onClick={() => handlePageChange(Math.min(Math.ceil(backups.length / rowsPerPage) - 1, page + 1))}
                    disabled={page >= Math.ceil(backups.length / rowsPerPage) - 1}
                    className="p-2 border rounded-lg text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== SCHEDULE TAB ==================== */}
        {activeTab === 'schedule' && (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Configuration Form */}
              <div className="lg:col-span-2 bg-slate-50/70 rounded-2xl p-4 sm:p-6 border border-slate-100 space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Automatic Backup Settings</h3>
                  <p className="text-xs text-slate-500">Choose when the system runs automated snapshots.</p>
                </div>

                {/* Animated Toggle Switch */}
                <div className="flex items-start gap-3 bg-white border rounded-xl p-4 shadow-sm">
                  <button
                    type="button"
                    onClick={handleToggleSchedule}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${settings.isEnabled ? 'bg-indigo-600' : 'bg-slate-200'}`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${settings.isEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                  <div className="space-y-0.5">
                    <span className="text-xs sm:text-sm font-bold text-slate-900">
                      {settings.isEnabled ? 'Automatic backups are ON' : 'Automatic backups are OFF'}
                    </span>
                    <p className="text-xs text-slate-500">
                      Enable this option to run system snapshots periodically without manual inputs.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Preset Selector */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Schedule Preset</label>
                    <select
                      value={isCustomMode ? 'custom' : settings.scheduleTime}
                      onChange={handlePresetChange}
                      disabled={!settings.isEnabled}
                      className="w-full border border-slate-200 bg-white rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:opacity-60"
                    >
                      {schedulePresets.map((preset) => (
                        <option key={preset.value} value={preset.value}>{preset.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Time Selection Block */}
                  {isCustomMode && (
                    <div className="bg-white rounded-xl border border-indigo-100 p-4 space-y-3 shadow-inner">
                      <span className="block text-xs font-bold text-slate-800">Set Custom Clock Time</span>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-slate-400 mb-1">Hour</label>
                          <select
                            value={customHour}
                            onChange={handleCustomTimeChange('hour')}
                            disabled={!settings.isEnabled}
                            className="w-full border border-slate-200 rounded-lg p-2 text-xs bg-slate-50 focus:outline-none"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-slate-400 mb-1">Minute</label>
                          <select
                            value={customMinute}
                            onChange={handleCustomTimeChange('minute')}
                            disabled={!settings.isEnabled}
                            className="w-full border border-slate-200 rounded-lg p-2 text-xs bg-slate-50 focus:outline-none"
                          >
                            {Array.from({ length: 60 }, (_, i) => (
                              <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 p-2.5 rounded-lg font-medium flex items-center justify-between">
                        <span>Selected daily time target:</span>
                        <code className="bg-white border px-2 py-0.5 rounded font-bold font-mono">{formatTimeDisplay(settings.scheduleTime)}</code>
                      </div>
                    </div>
                  )}

                  {isSpecialPreset && (
                    <div className="bg-sky-50 border border-sky-100 text-sky-900 rounded-xl p-3.5 text-xs font-medium">
                      Interval selected: <span className="font-bold underline">{schedulePresets.find(p => p.value === settings.scheduleTime)?.label}</span>
                    </div>
                  )}

                  {/* Settings Parameters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Timezone Context</label>
                      <select
                        value={settings.timezone}
                        onChange={handleSettingsChange('timezone')}
                        disabled={!settings.isEnabled}
                        className="w-full border border-slate-200 bg-white rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                      >
                        {timezones.map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Retention Days</label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={settings.retentionDays}
                        onChange={handleSettingsChange('retentionDays')}
                        disabled={!settings.isEnabled}
                        className="w-full border border-slate-200 bg-white rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                      />
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                    <button
                      onClick={handleRunBackupNow}
                      disabled={isLoading || isRestoring}
                      className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 border border-slate-200 bg-white text-slate-700 font-bold text-xs sm:text-sm rounded-xl hover:bg-slate-50 active:bg-slate-100 transition shadow-sm"
                    >
                      Run Now
                    </button>
                    
                    <button
                      onClick={handleUpdateSchedule}
                      disabled={isLoading || isRestoring}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2 text-xs sm:text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 active:bg-indigo-800 transition shadow-sm disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      )}
                      <span>Save Schedule</span>
                    </button>
                  </div>

                </div>
              </div>

              {/* Status Sidecar Dashboard */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 space-y-4">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Schedule Status</h4>
                
                <div className="divide-y divide-slate-200/60 text-xs sm:text-sm">
                  {[
                    { label: 'Status', element: <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${settings.isEnabled ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`}>{settings.isEnabled ? 'Running' : 'Stopped'}</span> },
                    { label: 'Current Schedule', element: <span className="font-semibold font-mono text-slate-900">{formatTimeDisplay(settings.scheduleTime)}</span> },
                    { label: 'Next Execution', element: <span className="font-semibold text-slate-700 text-right">{scheduler?.nextExecution ? format(new Date(scheduler.nextExecution), 'MMM dd, HH:mm:ss') : 'Not scheduled'}</span> },
                    { label: 'Retention Rule', element: <span className="font-semibold text-slate-700">Keep last {settings.retentionDays} days</span> },
                    { label: 'Selected Timezone', element: <span className="font-mono bg-slate-200/60 px-1.5 py-0.5 rounded text-xs text-slate-800 font-bold">{settings.timezone}</span> }
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between items-center py-2.5 first:pt-0 last:pb-0">
                      <span className="text-slate-500 font-medium">{row.label}</span>
                      {row.element}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ==================== LOGS TAB ==================== */}
        {activeTab === 'logs' && (
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Backup Logs</h3>
                <p className="text-xs text-slate-500">Track and filter operational logging outputs by date.</p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={logsDate}
                  onChange={(e) => setLogsDate(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <button
                  onClick={() => fetchLogs(logsDate)}
                  disabled={logsLoading}
                  className="inline-flex items-center justify-center px-3.5 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 active:bg-indigo-800 transition disabled:opacity-50 shadow-sm"
                >
                  {logsLoading ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Search</span>
                  )}
                </button>
              </div>
            </div>

            {/* Terminal Window View */}
            <div className="bg-slate-950 rounded-2xl p-4 shadow-xl border border-slate-900 relative group">
              <div className="flex gap-1.5 absolute top-3 right-4 opacity-40 group-hover:opacity-100 transition">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              </div>
              <div className="max-h-[420px] overflow-auto pt-3 font-mono text-left text-xs text-slate-300 whitespace-pre-wrap leading-relaxed selection:bg-slate-700">
                {logsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : logs ? (
                  logs
                ) : (
                  <p className="text-slate-600 italic text-center py-12">No data logs found for this specific date query.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ==================== ACTION CONFIRMATION MODAL ==================== */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl border ${confirmDialog.action === 'delete' ? 'bg-rose-50 border-rose-100 text-rose-600' : 'bg-indigo-50 border-indigo-100 text-indigo-600'}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <h3 className="text-base sm:text-lg font-bold tracking-tight text-slate-950">Confirm Action</h3>
            </div>
            
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium bg-slate-50 p-3 rounded-xl border border-slate-100 whitespace-pre-line">
              {confirmDialog.message}
            </p>
            
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={closeConfirmDialog}
                disabled={isLoading}
                className="px-3.5 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              
              <button
                onClick={handleConfirmAction}
                disabled={isLoading || isRestoring}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-bold text-white rounded-xl transition shadow-sm ${
                  confirmDialog.action === 'delete' ? 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800' : 'bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800'
                } disabled:opacity-50`}
              >
                {isLoading && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                <span>{confirmDialog.action === 'delete' ? 'Delete' : 'Confirm'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManagement;